import { Timestamp } from "firebase/firestore";
import { JobV2, JobFile } from "@/app/@types/job";
import {
  JobBasic,
  JobBasicFile,
  JobBasicLineItem,
} from "@/app/@types/jobBasic";
import { isSpecimenLineItem } from "./lineItem";
import {
  calculateDisplayGrandTotal,
  calculateDisplayRemainingBalance,
} from "./math";

// Same numbers the dashboard used to compute from the full job. Omitted if
// the job's data can't produce them; the dashboard then reads full docs.
const displayTotals = (
  job: JobV2
): { displayGrandTotal?: number; displayRemainingBalance?: number } => {
  try {
    return {
      displayGrandTotal: calculateDisplayGrandTotal(
        job.lineItems,
        job.subtotalAfterDiscounts,
        job.totalTax,
        job.grandTotal
      ),
      displayRemainingBalance: calculateDisplayRemainingBalance(
        job.lineItems,
        job.subtotalAfterDiscounts,
        job.totalTax,
        job.grandTotal,
        job.payments ?? [],
        job.remainingBalance
      ),
    };
  } catch {
    return {};
  }
};

// Client-side twin of buildJobBasic in functions/src/helpers/jobsIndex.ts.
// Used for optimistic index patches after our own mutations (the trigger's
// real write lands 1-10s later and confirms) and for mapping pendingJobs
// docs into the merged list. KEEP IN SYNC with the functions builder.

const toTimestamp = (v: any): Timestamp | null => {
  if (v === null || v === undefined) return null;
  if (v instanceof Timestamp) return v;
  if (v instanceof Date) return Timestamp.fromDate(v);
  if (typeof v.toMillis === "function") {
    return Timestamp.fromMillis(v.toMillis());
  }
  if (typeof v.seconds === "number") {
    return new Timestamp(v.seconds, v.nanoseconds ?? 0);
  }
  if (typeof v._seconds === "number") {
    return new Timestamp(v._seconds, v._nanoseconds ?? 0);
  }
  if (typeof v === "number") return Timestamp.fromMillis(v);
  return null;
};

const mapFiles = (files: unknown): JobBasicFile[] => {
  if (!Array.isArray(files)) return [];
  return (files as JobFile[])
    .filter((f) => f && typeof f.url === "string" && f.url.length > 0)
    .map((f) => ({ url: f.url, name: f.name ?? "" }));
};

export const jobV2ToBasic = (job: JobV2): JobBasic => {
  const lineItems: JobBasicLineItem[] = (job.lineItems ?? []).map((item) => {
    if (isSpecimenLineItem(item)) {
      return {
        uid: item.uid,
        frontFacingId: item.frontFacingId ?? "",
        quantity: item.quantity ?? 1,
        specimen: {
          species: item.specimen?.species ?? null,
          mountType: item.specimen?.mountType ?? null,
          parts: (item.specimen?.parts ?? []).map((p) => ({
            uid: p.uid,
            name: p.name ?? "",
            locationCode: p.locationCode ?? null,
          })),
        },
        stage: item.stage ?? null,
        tags: item.tags ?? [],
        notes: item.notes ?? "",
        files: mapFiles(item.files),
        estimatedCompletedAt: toTimestamp(item.estimatedCompletedAt),
      };
    }
    return {
      uid: item.uid,
      service: item.service ?? "",
      ticketNumber: item.ticketNumber ?? null,
      tags: item.tags ?? [],
      notes: item.notes ?? "",
      files: mapFiles(item.files),
    };
  });

  const lastHistory =
    job.history && job.history.length > 0
      ? job.history[job.history.length - 1]
      : null;

  return {
    uid: job.uid,
    jobId: job.jobId,
    jobStatus: job.jobStatus,
    client: {
      uid: job.client?.uid ?? "",
      firstName: job.client?.firstName ?? "",
      lastName: job.client?.lastName ?? "",
      fullName: job.client?.fullName ?? "",
      currency: job.client?.currency ?? null,
    },
    priority: job.priority ?? null,
    tags: job.tags ?? [],
    notes: job.notes ?? "",
    files: mapFiles(job.files),
    lineItems,
    paymentStatus: job.paymentStatus ?? null,
    remainingBalance: job.remainingBalance ?? 0,
    createdAt: toTimestamp(job.createdAt) ?? Timestamp.fromMillis(0),
    updatedAt: toTimestamp(job.updatedAt),
    completedAt: toTimestamp(job.completedAt),
    paymentDates: (job.payments ?? [])
      .filter((p) => !p.deleted)
      .map((p) => toTimestamp(p.date))
      .filter((ts): ts is Timestamp => ts !== null),
    contract: job.contract
      ? {
          signatureRequired: job.contract.signatureRequired ?? false,
          isSigned: job.contract.isSigned ?? false,
          hasPaymentPlan: job.contract.hasPaymentPlan ?? false,
          stripePaymentMethodId: job.contract.stripePaymentMethodId ?? null,
          paymentSchedule: (job.contract.paymentSchedule ?? []).map((s) => ({
            method: s.method ?? null,
            status: s.status,
            paymentNumber: s.paymentNumber,
          })),
          failedPayments: (job.contract.failedPayments ?? []).map((f) => ({
            paymentNumber: f.paymentNumber,
            paymentMethodId: f.paymentMethodId ?? null,
            date: toTimestamp(f.date),
          })),
        }
      : null,
    // Enough for the board to flag an unsigned wildlife record
    wrd: job.wrd
      ? {
          required: job.wrd.required ?? false,
          isSigned: job.wrd.isSigned ?? false,
        }
      : null,
    lastChange: lastHistory
      ? {
          byUid: lastHistory.teamMember?.uid ?? "",
          byName:
            lastHistory.teamMember?.fullName ||
            `${lastHistory.teamMember?.firstName ?? ""} ${
              lastHistory.teamMember?.lastName ?? ""
            }`.trim(),
          at: toTimestamp(lastHistory.changeTime),
        }
      : null,
    ...displayTotals(job),
  };
};
